/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {makeStyles} from '@material-ui/styles';
import clsx from 'clsx';
import isNil from 'lodash.isnil';
import {getAssetState, getAssetStateLabel} from '@ion/core/src/entities/Asset';
import {translate} from '@ion/core/src/locale/locale';
import {AssetState, CVSAssetType} from '@ion/core/src/types/entities';
import {getEventSpeed} from '@ion/core/src/units/helpers';
import {isTrue} from '@ion/core/src/utils/BooleanUtils';
import {isNonZeroNumber} from '@ion/core/src/utils/NumberUtil';
import {isEmpty, padDelimiterLeft} from '@ion/core/src/utils/StringUtil';
import useRootManager from '@ion/dls/src/client/useRootManager';
import ChargeLevel from '@ion/dls/src/components/chargelevel/ChargeLevel';
import {validateBatteryPercentage} from '@ion/dls/src/components/chargelevel/chargeLevelUtils';
import {DeviceInputContent} from '@ion/dls/src/components/device/DeviceInputContent';
import AssetFuelLevel from '@ion/dls/src/components/fuellevel/AssetFuelLevel';
import SeatbeltIcon from '@ion/dls/src/components/seatbeltIcon/SeatbeltIcon';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {TooltipWrapper} from '@ion/dls/src/components/tooltip/TooltipWrapper';
import {AssetDriverIcon} from '@ion/dls/src/icons/AssetDriverIcon';
import {AssetStatusIcon} from '@ion/dls/src/icons/AssetStatusIcon';
import {PoorGpsIcon} from '@ion/dls/src/icons/PoorGpsIcon';
import {SystemDateTimeIcon} from '@ion/dls/src/icons/SystemDateTimeIcon';
import Address from '@ion/dls/src/maps/components/Address';
import {useAssetSpeed} from '@ion/dls/src/maps/hooks/useAssetSpeed';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';
import {formatAddress, formatSpeed, getFormattedDate} from '@ion/dls/src/utils/ComponentUtils';
import FindNearByPopupContent, {OnFindNearByProps} from '../FindNearByPopupContent';

export interface AssetDetailPopupProps {
  asset: any;
  showFindNearBy: boolean;
  onFindNearBySubmit?: (props: OnFindNearByProps) => void;
  onCancelFindNearBy: () => void;
}

const useStyles = makeStyles(() => ({
  assetPopupContentWrapper : {
    width   : '100%',
    padding : '8px 12px'
  },
  assetName : {
    ...dlsAlt('typography', 'label3'),
    color         : dlsColor('aaBlue'),
    fontWeight    : '700 !important' as any,
    margin        : '0 !important',
    fontSize      : '12px !important',
    textTransform : 'uppercase'
  },
  operatorText : {
    ...dlsAlt('typography', 'label3'),
    color         : dlsColor('grey3'),
    margin        : '0 !important',
    fontSize      : '10px !important',
    fontWeight    : 500,
    textTransform : 'uppercase'
  },
  chargeFuelContainer : {
    display        : 'flex',
    flexDirection  : 'row',
    alignItems     : 'center',
    justifyContent : 'center'
  },
  assetStatusContainer : {
    height          : 30,
    width           : 258,
    display         : 'flex',
    alignItems      : 'center',
    padding         : '0 4px 0 12px',
    justifyContent  : 'space-between',
    color           : dlsColor('white'),
    backgroundColor : dlsColor('grey3'),
    fontSize        : '12px !important'
  },
  assetStatusIconContainer : {
    ...dlsAlt('typography', 'cta2'),
    display    : 'flex',
    gap        : 5,
    alignItems : 'center'
  },
  assetStatus : {
    fontWeight : 600
  },
  width100 : {
    width : '100%'
  },
  center : {
    display    : 'flex',
    alignItems : 'center'
  },
  eventTimeContainer : {
    display      : 'flex',
    alignItems   : 'center',
    marginBottom : 2
  },
  eventTime : {
    ...dlsAlt('typography', 'label3'),
    fontSize   : '11px !important',
    lineHeight : '18px !important',
    fontWeight : 700,
    color      : dlsColor('grey2'),
    marginLeft : 5
  },
  fuelText : {
    ...dlsAlt('typography', 'cta2'),
    color       : `${dlsColor('white')} !important`,
    fontWeight  : '700 !important',
    paddingLeft : '4px'
  },
  addressContainer : {
    color         : `${dlsColor('grey3')} !important`,
    textTransform : 'uppercase'
  },
  driverIconContainer : {
    display    : 'flex',
    alignItems : 'center'
  },
  driverIcon : {
    margin : '0px 2px 0px -3px'
  },
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'addressTooltip')
  },
  iconSpan : {
    display     : 'flex',
    position    : 'relative',
    top         : 1,
    marginRight : 3
  },
  inputs : {
    marginTop : 8
  }
}));

/**
 * @AssetDetailPopup
 * Functional to display the asset detail content for the selected asset in the popup
 * find nearby.
 *  ```typescript
 *   import AssetDetailPopup from '@/routes/tracking/maps/AssetDetailPopup';
 *
 *   <AssetDetailPopup asset={asset} showFindNearBy={false} onFindNearBySubmit={function(){}}/>
 *  ```
 */
const AssetDetailPopup = ({asset, showFindNearBy, onFindNearBySubmit, onCancelFindNearBy}: AssetDetailPopupProps) => {
  const styles                                = useStyles();
  const [showToolTip, setShowToolTip]         = useState(false);
  const [showIconToolTip, setShowIconToolTip] = useState(false);
  const {getContainer}                        = useRootManager(false);
  const getSpeed                              = useAssetSpeed();

  const {
    assetAlias,
    assetName,
    batteryRemainingPercent,
    evBatteryPackRemainingCharge,
    city,
    eventTime,
    operatorNames,
    state,
    street,
    gpsValidity,
    latitude,
    longitude
  } = asset;

  const getSpeedVal = useCallback((selectedAsset: CVSAssetType): string => {
    const {speed, units} = getEventSpeed(selectedAsset as any);
    return formatSpeed(speed, units);
  }, [getEventSpeed, formatSpeed]);

  const empty             = translate('maps:popup.empty');
  const mappedAssetStatus = getAssetState(asset);
  const speedVal          = getSpeed(asset, getSpeedVal(asset));
  const assetStateLabel   = getAssetStateLabel(mappedAssetStatus);
  const operatorText      = useMemo(() => !isNil(operatorNames) && operatorNames, [operatorNames]);
  const batteryPercentage = validateBatteryPercentage(evBatteryPackRemainingCharge, batteryRemainingPercent);

  const seatbeltStatus = useMemo(() => (
    mappedAssetStatus === AssetState.PARKED
      ? undefined
      : asset.seatbeltstatus ?? asset.seatBeltStatus
  ), [mappedAssetStatus, asset]);

  const assetStatusSvgStyle = useMemo(() => ({
    border       : `1px solid ${dlsColor('white')}`,
    height       : 12,
    width        : 12,
    borderRadius : '50%',
    borderWidth  : '2px'
  }), []);

  useEffect(() => {
    return () => {
      setShowToolTip(false);
      setShowIconToolTip(false);
    };
  }, []);

  const setToolTip = useCallback(({type}) => {
    setShowToolTip(!(type === 'click' || type === 'mouseleave'));
  }, [showToolTip]);

  const setIconToolTip = useCallback(({type}) => {
    setShowIconToolTip(!(type === 'click' || type === 'mouseleave'));
  }, [showIconToolTip]);

  const addressTooltip = useMemo(() => {
    const isValidGPS = isNonZeroNumber(latitude) && isNonZeroNumber(longitude);
    if (isValidGPS) {
      const title = formatAddress({street, city, state});
      const poorGpsTooltip = translate('tracking:list.detailed.poorGpsQuality');
      return (
        <div id='asset-detail-popup' className={clsx([styles.width100, styles.center])}>
          {!isTrue(gpsValidity) && (
            <Tooltip id='asset-detail-gps-popup'
              title={showIconToolTip && poorGpsTooltip}
              open={showIconToolTip}
              placement={'top'}
              arrow >
              <span className={clsx([styles.iconSpan])}
                onMouseEnter={setIconToolTip}
                onMouseLeave={setIconToolTip}
                onClick={setIconToolTip}>
                <PoorGpsIcon />
              </span>
            </Tooltip>
          )}
  
          <TooltipWrapper title={showToolTip && title}
            container={getContainer('base-container')}
            open={showToolTip}>
            <div onMouseEnter={setToolTip}
              onMouseLeave={setToolTip}
              onClick={setToolTip}
              className={clsx([styles.width100, styles.addressContainer])}>
              <Address street={street}
                state={state}
                city={city}
                isPopupVariant />
            </div>
          </TooltipWrapper>
        </div>
  
      );
    }
  }, [street, state, city, showToolTip, showIconToolTip, latitude, longitude]);

  return (
    <div style={{width: '100%'}}
      onMouseLeave={setToolTip}
      onClick={setToolTip} >
      {!showFindNearBy && (
        <>
          <div className={clsx([styles.assetStatusContainer])}>
            <span className={styles.assetStatusIconContainer}>
              {mappedAssetStatus && (<AssetStatusIcon status={mappedAssetStatus} style={assetStatusSvgStyle} />)}
              <span className={styles.assetStatus}>
                {!isEmpty(assetStateLabel) ? assetStateLabel?.toUpperCase() : translate('common:notAvailable')} {speedVal ?? ''}
              </span>
            </span>
            <span className={styles.chargeFuelContainer}>
              <ChargeLevel charge={batteryPercentage} />
              <SeatbeltIcon status={seatbeltStatus} color={dlsColor('white')} size={IconSize.smallMedium} spacing={5}/>
              <AssetFuelLevel asset={asset}
                classNames={[styles.fuelText, styles.assetStatus]}
                isHidden={!isNil(evBatteryPackRemainingCharge)}
                color={dlsColor('white')} />
            </span>
          </div>
          <div className={styles.assetPopupContentWrapper}>
            <div className={styles.width100}>
              <div className={styles.assetName}>
                {padDelimiterLeft(assetName) || empty}
              </div>
            </div>
            <div className={clsx([styles.width100, styles.driverIconContainer])}>
              {!!operatorText && (
                <div className={styles.driverIcon}>
                  <AssetDriverIcon size={20}/>
                </div>
              )}
              <div className={styles.operatorText}>
                {operatorText || assetAlias || ''}
              </div>
            </div>
            <div className={clsx([styles.eventTimeContainer, styles.width100])}>
              <SystemDateTimeIcon color={dlsColor('grey1')} />
              <div className={styles.eventTime}>
                {getFormattedDate(eventTime).toUpperCase()}
              </div>
            </div>
            {addressTooltip}
            <div className={styles.inputs}>
              <DeviceInputContent asset={asset} popupVariant />
            </div>
          </div>
        </>
      )}
      {showFindNearBy && (
        <FindNearByPopupContent asset={asset}
          onFindButtonClick={onFindNearBySubmit}
          onCancel={onCancelFindNearBy} />
      )}
    </div>
  );
};
export default AssetDetailPopup;
